import React, { createContext, useContext, useEffect, useState } from "react";
import { Download, RefreshCw, WifiOff, X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface PWAContextType {
  isInstalled: boolean;
  canInstall: boolean;
  isOnline: boolean;
  hasUpdate: boolean;
  installApp: () => Promise<void>;
  applyUpdate: () => void;
}

const PWAContext = createContext<PWAContextType>({
  isInstalled: false,
  canInstall: false,
  isOnline: true,
  hasUpdate: false,
  installApp: async () => {},
  applyUpdate: () => {},
});

export function PWAProvider({ children }: { children: React.ReactNode }) {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isOnline, setIsOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [dismissOffline, setDismissOffline] = useState(false);

  useEffect(() => {
    // 1. Detect if already installed (standalone mode)
    if (typeof window !== "undefined") {
      const isStandalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        (window.navigator as any).standalone === true;
      setIsInstalled(isStandalone);
    }

    // 2. Register Service Worker
    if (typeof window !== "undefined" && "serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/sw.js")
        .then((reg) => {
          setRegistration(reg);

          // Check for worker updates
          reg.addEventListener("updatefound", () => {
            const newWorker = reg.installing;
            if (newWorker) {
              newWorker.addEventListener("statechange", () => {
                if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
                  setHasUpdate(true);
                }
              });
            }
          });
        })
        .catch((err) => {
          console.warn("[PWA] Service Worker registration failed:", err);
        });

      // Handle controllerchange (page refresh on update)
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        window.location.reload();
      });
    }

    // 3. Listen for BeforeInstallPromptEvent
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    // 4. Listen for appinstalled
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    // 5. Online/Offline events
    const handleOnline = () => {
      setIsOnline(true);
      setDismissOffline(false);
    };
    const handleOffline = () => {
      setIsOnline(false);
      setDismissOffline(false);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleAppInstalled);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleAppInstalled);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const installApp = async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    } catch (err) {
      console.error("[PWA] Install prompt failed:", err);
    }
  };

  const applyUpdate = () => {
    if (registration?.waiting) {
      registration.waiting.postMessage({ type: "SKIP_WAITING" });
    } else {
      window.location.reload();
    }
  };

  return (
    <PWAContext.Provider
      value={{
        isInstalled,
        canInstall: Boolean(deferredPrompt && !isInstalled),
        isOnline,
        hasUpdate,
        installApp,
        applyUpdate,
      }}
    >
      {/* Offline Status Warning Bar */}
      {!isOnline && !dismissOffline && (
        <div className="bg-amber-600 text-white px-4 py-2 text-xs flex items-center justify-between shadow-md z-50 sticky top-0 transition-all">
          <div className="flex items-center gap-2 font-medium">
            <WifiOff className="size-4 animate-pulse shrink-0" />
            <span>You are currently offline. Viewing cached records. Actions will resume when reconnected.</span>
          </div>
          <button
            type="button"
            onClick={() => setDismissOffline(true)}
            className="p-1 hover:bg-amber-700 rounded-md transition-colors"
            title="Dismiss"
          >
            <X className="size-3.5" />
          </button>
        </div>
      )}

      {/* New App Version Available Notification */}
      {hasUpdate && (
        <div className="fixed bottom-4 right-4 z-50 bg-card border border-primary/30 rounded-xl p-4 shadow-2xl flex items-center gap-3.5 animate-in slide-in-from-bottom-5">
          <div className="p-2 bg-primary/10 rounded-lg text-primary">
            <RefreshCw className="size-4 animate-spin" />
          </div>
          <div className="text-xs">
            <div className="font-semibold text-foreground">Update Available</div>
            <div className="text-muted-foreground">A new version of Nelgates CRM is ready.</div>
          </div>
          <Button size="sm" onClick={applyUpdate} className="h-8 text-xs gap-1.5 ml-2 cursor-pointer">
            Refresh
          </Button>
        </div>
      )}

      {children}
    </PWAContext.Provider>
  );
}

export function usePWA() {
  return useContext(PWAContext);
}
